import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ReportAccount, ReportsService } from 'src/app/services/reports.service';

interface AgentConfigPayload {
  account: {
    account_id: string;
    name: string;
  };
  account_config: Array<{
    account_id: string;
    account_config_key: string;
    config: Record<string, string | number | null>;
  }>;
}

interface AccountChannel {
  accountId: string;
  accountName: string;
  channelType: string;
  channelValue: string;
}

@Component({
  selector: 'app-agent-configs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    SelectModule,
    TableModule,
    DialogModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './agent-configs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgentConfigsComponent implements OnInit {
  agentConfig = {
    accountId: '',
    accountName: '',
    mobydygShopId: null as number | null,
    infobipApiKey: '',
    infobipBaseUrl: '',
    infobipSender: '',
    chatwootBaseUrl: '',
    chatwootApiKey: '',
    chatwootAccountId: null as number | null
  };

  accounts: ReportAccount[] = [];
  accountsLoading = false;

  channelTypeOptions = [
    { label: 'WhatsApp', value: 'whatsapp' },
    { label: 'Instagram', value: 'instagram' },
    { label: 'Facebook', value: 'facebook' },
    { label: 'Messenger', value: 'messenger' },
    { label: 'Telegram', value: 'telegram' },
    { label: 'Livechat', value: 'livechat' }
  ];

  channels: AccountChannel[] = [];
  showChannelDialog = false;
  newChannel = {
    accountId: null as string | null,
    channelType: null as string | null,
    channelValue: ''
  };

  constructor(
    private readonly reportsService: ReportsService,
    private readonly messageService: MessageService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    void this.loadAccounts();
  }

  private async loadAccounts(): Promise<void> {
    this.accountsLoading = true;
    this.cdr.markForCheck();

    try {
      const response = await this.reportsService.getReports();
      this.accounts = response.accounts;
    } catch (error) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las cuentas' });
    } finally {
      this.accountsLoading = false;
      this.cdr.markForCheck();
    }
  }

  openChannelDialog(): void {
    this.newChannel = { accountId: null, channelType: null, channelValue: '' };
    this.showChannelDialog = true;
  }

  closeChannelDialog(): void {
    this.showChannelDialog = false;
  }

  createChannel(): void {
    if (!this.newChannel.accountId || !this.newChannel.channelType || !this.newChannel.channelValue) {
      return;
    }

    const account = this.accounts.find(a => a.accountId === this.newChannel.accountId);

    this.channels = [
      ...this.channels,
      {
        accountId: this.newChannel.accountId,
        accountName: account?.name ?? this.newChannel.accountId,
        channelType: this.newChannel.channelType,
        channelValue: this.newChannel.channelValue
      }
    ];

    this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Canal agregado' });
    this.closeChannelDialog();
    this.cdr.markForCheck();
  }

  sendAgentConfig(): AgentConfigPayload {
    const payload: AgentConfigPayload = {
      account: {
        account_id: this.agentConfig.accountId,
        name: this.agentConfig.accountName
      },
      account_config: [
        {
          account_id: this.agentConfig.accountId,
          account_config_key: 'mobydyg',
          config: { ShopId: this.agentConfig.mobydygShopId }
        },
        {
          account_id: this.agentConfig.accountId,
          account_config_key: 'infobip',
          config: {
            ApiKey: this.agentConfig.infobipApiKey,
            BaseUrl: this.agentConfig.infobipBaseUrl,
            Sender: this.agentConfig.infobipSender
          }
        },
        {
          account_id: this.agentConfig.accountId,
          account_config_key: 'chatwoot',
          config: {
            baseUrl: this.agentConfig.chatwootBaseUrl,
            apiKey: this.agentConfig.chatwootApiKey,
            accountId: this.agentConfig.chatwootAccountId
          }
        }
      ]
    };

    return payload;
  }
}
